"use client";

import { useState, useContext, useEffect } from "react";
import Slider, { Settings } from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import clsx from "clsx";
import Link from "next/link";
import MinusIcon from "@/assets/icons/minus";
import PlusIcon from "@/assets/icons/plus";
import {
  ProductDetailsResponse,
  ProductSpecificationInterface,
  ProductVariantInterface,
} from "@/types/product";
import { CartContext } from "@/global-store/cart-data";
import DeleteIcon from "@/assets/icons/delete";
import { CartInterface, ProductCartVariantInterface } from "@/types/cart";
import { RelatedItems } from "@/app/cart/components/related-items";
import { priceSymbol } from "@/utils/price-count";
import {
  formateProductSpecification,
  formateProductVariant,
  isBothCartVariantSame,
} from "@/utils/formate-product-variant";
import { IconButton } from "../icon-button";
import { Typography } from "../typography";
import BreadCrumb from "../breadcrumb/breadcrumb";
import ProductImage from "./ProductImage";

export const ProductDetails = ({
  product,
  relatedProducts,
}: {
  product: ProductDetailsResponse;
  relatedProducts: ProductDetailsResponse[];
}) => {
  const [nav1, setNav1] = useState<Slider | null>(null);
  const [nav2, setNav2] = useState<Slider | null>(null);
  const [selected, setSelected] = useState<ProductCartVariantInterface[]>([]);
  const [currentProduct, setCurrentProduct] = useState<
    CartInterface | undefined
  >();
  const { cartData, addToCart, incQuantity, decQuantity, removeCartData } =
    useContext(CartContext);

  const productVariants: ProductCartVariantInterface[] = formateProductVariant(
    product.variants as ProductVariantInterface[],
  );

  const productSpecification = formateProductSpecification(
    product.properties as ProductSpecificationInterface[],
  );
  const handelSetDefaultVariantValue = () => {
    if (!currentProduct) {
      const values = productVariants
        .filter((item) => item.variantId.length)
        .map((item) => ({ ...item, variantId: [item.variantId[0]] }));

      if (values.length) {
        return setSelected(values);
      }
    }

    const selectedData = currentProduct ? currentProduct.variants : [];
    return setSelected(selectedData as ProductCartVariantInterface[]);
  };

  const getCartData = () => {
    const isCartData = cartData.find(({ slug, variants }) => {
      const isSame = isBothCartVariantSame(variants, selected);
      return isSame && slug === product.slug;
    });

    setCurrentProduct(isCartData);
  };

  const handleAddToCart = () => {
    addToCart({
      ...product,
      quantity: 1,
      variants: selected,
    });
  };

  const productImages = product?.images?.length
    ? product.images
    : [product.image];

  const handleSelectVariant = (variant: ProductCartVariantInterface) => {
    setSelected((prev) => {
      const selectedIndex = prev.findIndex(
        (item) => item.groupId.id === variant.groupId.id,
      );
      if (selectedIndex >= 0) {
        const updatedValue = [...prev];
        updatedValue[selectedIndex] = {
          ...variant,
        };

        return updatedValue;
      }
      return prev;
    });
  };
  useEffect(() => {
    handelSetDefaultVariantValue();
  }, []);

  useEffect(() => {
    getCartData();
  }, [selected, cartData]);

  const handleDecrement = (
    id: number,
    variants: ProductCartVariantInterface[],
    quantity: number,
  ) => decQuantity(id, variants, quantity);

  const handleDelete = (id: number, variants: ProductCartVariantInterface[]) =>
    removeCartData(id, variants);

  const handleIncrement = (
    id: number,
    variants: ProductCartVariantInterface[],
    quantity: number,
  ) => {
    incQuantity(id, variants, quantity);
  };

  const mainSliderSettings: Settings = {
    asNavFor: nav2 as Slider,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    infinite: productImages.length > 1,
  };

  const thumbnailSliderSettings: Settings = {
    asNavFor: nav1 as Slider,
    slidesToShow: Math.min(productImages.length, 3),
    swipeToSlide: true,
    focusOnSelect: true,
    centerMode: true,
    centerPadding: "0px",
    arrows: false,
    infinite: productImages.length > 1,
  };

  return (
    <section className="bg-white pb-10">
      <div className="bg-gray-100 px-2 md:px-4">
        <BreadCrumb
          routes={[
            { name: "Home", href: "/" },
            { name: "Products", href: "/products" },
            { name: product.name, href: product.slug },
          ]}
        />
      </div>
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-center items-start gap-8 my-10">
          <div className="relative w-full md:w-3/5 lg:w-3/5 md:py-10">
            <Slider {...mainSliderSettings} ref={setNav1}>
              {productImages.map((image) => (
                <div
                  key={image}
                  className="flex justify-center items-center !rounded-xl"
                >
                  <div className="relative w-full h-96 !rounded-xl">
                    <ProductImage
                      src={image}
                      alt="Product Image"
                      className="!rounded-xl"
                    />
                  </div>
                </div>
              ))}
            </Slider>

            {productImages.length > 1 && (
              <div className="absolute z-10 w-full bottom-0 left-0 flex justify-center lg:-bottom-6">
                <div
                  className="transition-all rounded-full shadow-xl backdrop-blur-md overflow-hidden lg:rounded-2xl pl-4"
                  style={{
                    width: `calc(${productImages.length * 80}px + ${
                      (productImages.length + 1) * 8
                    }px)`,
                  }}
                >
                  <Slider {...thumbnailSliderSettings} ref={setNav2}>
                    {productImages.map((image, i) => (
                      <div
                        key={image}
                        className="flex justify-center items-center h-auto w-20"
                      >
                        <div className="relative h-20 w-20 flex justify-center items-center">
                          <ProductImage
                            src={image}
                            alt="Thumbnail Image"
                            className={clsx(
                              "aspect-square my-1 mx-2",
                              i === 0 && "ml-2",
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col w-full md:w-2/5 lg:w-2/5">
            <Typography
              tag="h1"
              className="font-semibold text-3xl 2xl:text-4xl mt-10 text-slate-900"
            >
              {product?.name
                ? product.name.charAt(0).toUpperCase() +
                  product.name.slice(1).toLowerCase()
                : ""}
            </Typography>
            <Typography
              tag="h6"
              className="text-2xl lg:text-3xl font-semibold my-2 text-primary"
            >
              {priceSymbol(product.price - product.flatDiscount)}
            </Typography>
            <Typography
              tag="p"
              className="mb-6 text-slate-800 text-sm lg:text-base break-words"
            >
              {product?.description}
            </Typography>
            <hr className="mb-4" />

            {productVariants.map(({ id, groupId, variantId }) => (
              <div className="flex items-stretch my-3 gap-2" key={id}>
                <Typography
                  tag="p"
                  className="mr-2 my-1 text-base sm:text-lg font-semibold text-gray-700 self-start min-w-20 w-auto"
                >
                  {groupId.name}:
                </Typography>
                <div className="flex items-center gap-3 flex-wrap">
                  {variantId.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() =>
                        handleSelectVariant({
                          groupId,
                          id,
                          variantId: [variant],
                          productId: product.id,
                        })
                      }
                      className={`transition-all whitespace-nowrap px-3 py-2 border rounded-lg mx-1 hover:bg-gray-100 font-semibold shadow-sm hover:shadow ${
                        selected.find(
                          (item) =>
                            item.variantId[0].variantId === variant.variantId,
                        )
                          ? "bg-primary hover:text-black text-white hover:bg-cyan-700 border-primary"
                          : "bg-white hover:text-black border-gray-300"
                      }`}
                    >
                      {variant.value}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex items-center my-4 gap-4">
              {currentProduct ? (
                <div className="flex items-center justify-between border border-gray-300 rounded-lg overflow-hidden max-w-[200px] w-full">
                  <IconButton
                    onClick={() =>
                      currentProduct.quantity === 1
                        ? handleDelete(
                            currentProduct.id,
                            currentProduct.variants,
                          )
                        : handleDecrement(
                            currentProduct.id,
                            currentProduct.variants,
                            currentProduct.quantity - 1,
                          )
                    }
                    size="large"
                    color="transparent"
                    className="transition-all rounded-none border-r hover:bg-primary hover:text-white h-full"
                  >
                    {currentProduct.quantity === 1 ? (
                      <DeleteIcon fill="red" />
                    ) : (
                      <MinusIcon />
                    )}
                  </IconButton>
                  <span className="text-lg sm:text-xl font-medium px-2 flex items-center justify-center">
                    {currentProduct.quantity}
                  </span>
                  <IconButton
                    onClick={() =>
                      handleIncrement(
                        currentProduct.id,
                        currentProduct.variants,
                        currentProduct.quantity + 1,
                      )
                    }
                    size="large"
                    color="transparent"
                    className="transition-all rounded-none border-l hover:bg-primary hover:text-white h-full"
                  >
                    <PlusIcon />
                  </IconButton>
                </div>
              ) : (
                <button
                  onClick={handleAddToCart}
                  className="bg-primary text-nowrap text-white font-semibold text-base sm:text-lg md:text-base lg:text-lg xl:text-xl px-10 md:px-8 py-2 rounded-lg transition-all h-12 shadow-md hover:bg-cyan-600 hover:scale-105 hover:shadow-lg active:scale-100 active:outline-none"
                >
                  Add To Cart
                </button>
              )}
              {currentProduct && currentProduct.quantity > 0 ? (
                <Link href="/cart">
                  <button className="bg-primary text-nowrap text-white font-semibold text-base sm:text-lg md:text-base lg:text-lg xl:text-xl px-10 md:px-8 py-2 rounded-lg transition-all h-12 shadow-md hover:bg-cyan-600 hover:scale-105 hover:shadow-lg active:scale-100 active:outline-none">
                    Go To Cart
                  </button>
                </Link>
              ) : null}
            </div>
          </div>
        </div>
        <hr className="mt-16" />
        {productSpecification.length ? (
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2 mt-10 px-2 sm:text-xl">
              <Typography tag="h3">Product Specification</Typography>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm w-full">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <tbody>
                    {productSpecification.map(({ groupId, valueId, id }) => (
                      <tr key={id}>
                        <td className="border p-2 font-medium w-1/2">
                          {groupId?.name}
                        </td>
                        <td className="border p-2 w-1/2">
                          {valueId?.map((value, index) => (
                            <Typography tag="span" key={value.id}>
                              {value.value}
                              {valueId.length && index !== valueId.length - 1
                                ? ", "
                                : null}
                            </Typography>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}

        {relatedProducts.length ? (
          <RelatedItems products={relatedProducts} />
        ) : null}
      </div>
    </section>
  );
};
